import type MarkdownIt from 'markdown-it'
import type StateInline from 'markdown-it/lib/rules_inline/state_inline.mjs'
import type { Delimiter } from 'markdown-it/lib/rules_inline/state_inline.mjs'

/**
 * `++underline++` support for markdown-it — the syntax the rich-text editor
 * serializes an underline mark to.
 *
 * Ported from markdown-it's own `~~strikethrough~~` rule: markers are pushed as
 * plain text tokens and recorded as delimiters, then converted to `u` tags once
 * the whole inline run is tokenized. Emitting the tags directly instead would
 * shift the token indices that emphasis delimiters point at, which silently
 * drops marks (`**++under++**` loses its underline).
 */
const MARKER = 0x2b /* + */
const MARKER_CHAR = '+'
const MARKUP = '++'

const tokenize = (state: StateInline, silent: boolean): boolean => {
  const start = state.pos

  if (silent) return false
  if (state.src.charCodeAt(start) !== MARKER) return false

  const scanned = state.scanDelims(state.pos, true)
  let len = scanned.length

  if (len < 2) return false

  if (len % 2) {
    state.push('text', '', 0).content = MARKER_CHAR
    len--
  }

  for (let i = 0; i < len; i += 2) {
    state.push('text', '', 0).content = MARKUP

    state.delimiters.push({
      marker: MARKER,
      // Disables the "rule of 3" length checks, which only apply to emphasis.
      length: 0,
      token: state.tokens.length - 1,
      end: -1,
      open: scanned.can_open,
      close: scanned.can_close,
    })
  }

  state.pos += scanned.length

  return true
}

const convertPairs = (state: StateInline, delimiters: Delimiter[]) => {
  const loneMarkers: number[] = []

  for (const startDelim of delimiters) {
    if (startDelim.marker !== MARKER) continue
    if (startDelim.end === -1) continue

    const endDelim = delimiters[startDelim.end]!

    const open = state.tokens[startDelim.token]!
    open.type = 'underline_open'
    open.tag = 'u'
    open.nesting = 1
    open.markup = MARKUP
    open.content = ''

    const close = state.tokens[endDelim.token]!
    close.type = 'underline_close'
    close.tag = 'u'
    close.nesting = -1
    close.markup = MARKUP
    close.content = ''

    const beforeClose = state.tokens[endDelim.token - 1]
    if (beforeClose?.type === 'text' && beforeClose.content === MARKER_CHAR) {
      loneMarkers.push(endDelim.token - 1)
    }
  }

  // An odd marker run splits as `+` + `++`, leaving a stray marker before the
  // closing tag — move it after any adjacent closes so it reads as text.
  while (loneMarkers.length) {
    const i = loneMarkers.pop()!
    let j = i + 1

    while (
      j < state.tokens.length &&
      state.tokens[j]!.type === 'underline_close'
    ) {
      j++
    }

    j--

    if (i !== j) {
      const token = state.tokens[j]!
      state.tokens[j] = state.tokens[i]!
      state.tokens[i] = token
    }
  }
}

const postProcess = (state: StateInline): boolean => {
  convertPairs(state, state.delimiters)

  for (const meta of state.tokens_meta) {
    if (meta?.delimiters) convertPairs(state, meta.delimiters)
  }

  return false
}

export const underlinePlugin = (md: MarkdownIt) => {
  md.inline.ruler.before('emphasis', 'underline', tokenize)
  md.inline.ruler2.before('emphasis', 'underline', postProcess)
}
